import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import QueryCard from 'components/Meteorology/Shared/QueryCard';
import QueryCardHeader from 'components/Meteorology/Shared/QueryCard/Header';
import QueryCardTrigger from 'components/Meteorology/Shared/QueryCard/Trigger';
import QueryCardChips from 'components/Meteorology/Shared/QueryCard/Chips';
import SubdivisaoUnificada from 'components/Windx/CabecalhoGrafico/SubdivisaoUnificada';
import Unit from 'components/Windx/CabecalhoGrafico/Unit';
import BarraConsulta from 'components/Windx/Mobile/BarraConsulta';
import ControlPanelSheet from 'components/Windx/Mobile/ControlPanelSheet';
import SeletorEntidadesSheet from 'components/Windx/Mobile/SeletorEntidadesSheet';
import ConsultaIncompletaCard, { ConsultaIncompletaItem } from 'components/Windx/Mobile/ConsultaIncompletaCard';
import useEstimativaWindx from 'helper/windx/useEstimativaWindx';
import { useGetPainelOptionsQuery } from 'services/api/windx';
import {
  OpcaoRmv, Unidade, WindxTela, painelExibeTipoRodada,
} from 'helper/windx';
import {
  WindxGranularidadeSH,
  selectWindxIdSubdivisaoEspacial,
  selectWindxIdsEntidades,
  selectWindxIdsModelos,
  selectWindxGranularidadeSH,
  selectWindxOpcoesRmv,
  selectWindxTipoRodada,
  selectWindxUnidade,
  selectWindxFiltrosCompletos,
  setIdsEntidades,
  setUnidade,
  realizarConsulta,
} from 'redux/reducers/windxControlPanelReducer';
import { cssVar } from 'constants/theme';

import './index.scss';

interface CabecalhoGraficoMobileProps {
  tela: Extract<WindxTela, 'sh' | 'sm'>;
  children: React.ReactNode;
  /** `true` enquanto a query de dados da tela esta em voo (vai para a barra). */
  carregando?: boolean;
  // Se `true`, o seletor sinaliza "nenhuma marcada = todas as entidades".
  // Vale apenas para Comparativo SM.
  permiteSelecaoVazia?: boolean;
}

// Nota curta de granularidade no canto do cabecalho do card. O Comparativo
// usa `tela="sm"` e por isso tambem le "Semanal".
function granularidadeLabel(
  tela: Extract<WindxTela, 'sh' | 'sm'>,
  granularidade: WindxGranularidadeSH,
): string {
  if (tela === 'sm') return 'Semanal';
  return granularidade === 'diario' ? 'Diário' : 'Semi-horário';
}

// Resumo do que esta no painel, para o usuario nao precisar abri-lo so para
// conferir. Subdivisao e entidades ja aparecem no chip acima, entao aqui
// ficam modelos e RMV.
function resumoDoPainel(qtdModelos: number, opcoesRmv: OpcaoRmv[]): string {
  const modelos = qtdModelos === 0
    ? 'Nenhum modelo'
    : `${qtdModelos} modelo${qtdModelos === 1 ? '' : 's'}`;

  const comRmv = opcoesRmv.includes('com_rmv');
  const semRmv = opcoesRmv.includes('sem_rmv');
  let rmv = '';
  if (comRmv && semRmv) rmv = 'com e sem RMV';
  else if (comRmv) rmv = 'com RMV';
  else if (semRmv) rmv = 'sem RMV';

  return rmv ? `${modelos} · ${rmv}` : modelos;
}

// Texto de apoio acima do checklist quando faltam filtros obrigatorios. No
// Semanal/Comparativo com a etapa Tipo de Rodada (WEOL-SM #99) o tipo entra na
// lista; no SH ele ja faz parte do "estudo automatico completo".
function hintFiltrosIncompletos(
  tela: Extract<WindxTela, 'sh' | 'sm'>,
  exibeRunType: boolean,
): string {
  if (tela === 'sh') return 'Precisa de subdivisão, entidade e um estudo automático completo.';
  return exibeRunType
    ? 'Precisa de subdivisão, entidade, tipo de rodada e ao menos um modelo.'
    : 'Precisa de subdivisão, entidade e ao menos um modelo.';
}

// Descricao do estado de entidades, usada dentro do item combinado de
// subdivisao+entidades do checklist de filtros faltando.
function metaEntidades(qtd: number, permiteSelecaoVazia: boolean): string {
  if (qtd === 0) return permiteSelecaoVazia ? 'nenhuma = todas' : 'ao menos uma';
  return `${qtd} selecionada${qtd === 1 ? '' : 's'}`;
}

/**
 * Chrome das telas de grafico do Windx no mobile: card "Consulta atual",
 * barra fixa de consulta, painel de controle e seletor de entidades.
 *
 * Equivalente mobile de `Windx/CabecalhoGrafico`, com a mesma superficie de
 * props. Reusa os filhos do desktop que nao dependem de largura
 * (`SubdivisaoUnificada`, `BotaoDownload`) e o `QueryCard` compartilhado da
 * Meteorologia — decisao travada da iniciativa (2026-07-08).
 *
 * Por que a `BarraConsulta` mora aqui e nao na view: ela e o trigger do card
 * abrem o MESMO painel, entao o estado "painel aberto" precisa de dono unico.
 * Com a barra na view, ou o estado subiria (e o trigger viraria controlado,
 * espalhando a ligacao por 3 telas) ou existiriam dois estados concorrentes.
 */
export default function CabecalhoGraficoMobile({
  tela,
  children,
  carregando = false,
  permiteSelecaoVazia = false,
}: CabecalhoGraficoMobileProps) {
  const dispatch = useDispatch();
  const idSubdivisao = useSelector(selectWindxIdSubdivisaoEspacial);
  const idsEntidades = useSelector(selectWindxIdsEntidades);
  const unidade = useSelector(selectWindxUnidade);
  const filtrosCompletos = useSelector(selectWindxFiltrosCompletos);
  const idsModelos = useSelector(selectWindxIdsModelos);
  const granularidade = useSelector(selectWindxGranularidadeSH);
  const opcoesRmv = useSelector(selectWindxOpcoesRmv);
  const tipoRodada = useSelector(selectWindxTipoRodada);
  const { estimativa } = useEstimativaWindx();
  const { data: painelOptions } = useGetPainelOptionsQuery(tela);
  // WEOL-SM (#99): a etapa Tipo de Rodada existe no painel desta aba? Mesma regra
  // do painel (`exibeRunType` do useControlPanelRules): SH sempre; Semanal e
  // Comparativo (que usa tela="sm") so com a flag do BE ligada. Sem a etapa, o
  // painel assume 'final' e nao ha tipo para o usuario escolher.
  const exibeRunType = painelExibeTipoRodada(painelOptions, tela);

  const granNote = granularidadeLabel(tela, granularidade);
  const resumoPainel = resumoDoPainel(idsModelos.length, opcoesRmv);

  const [painelAberto, setPainelAberto] = useState(false);
  const [seletorAberto, setSeletorAberto] = useState(false);
  const raizRef = useRef<HTMLElement>(null);

  const subdivisaoAtual = painelOptions?.subdivisoes_espaciais.find(
    (s) => s.id === idSubdivisao,
  );

  // `SubdivisaoUnificada` nao expoe API externa para abrir seu proprio
  // BottomSheet — disparamos um click no chip toggle (mesmo elemento que o
  // usuario tocaria).
  const abrirSubdivisao = () => {
    raizRef.current
      ?.querySelector<HTMLButtonElement>('[data-testid="windx-subdivisao-unificada-toggle"]')
      ?.click();
  };

  // Checklist do card "Falta configurar a consulta" (so aparece quando
  // `!filtrosCompletos`). Cada item leva direto ao controle que falta —
  // painel de controle ou seletor de entidades — em vez de deixar o
  // usuario procurar sozinho (espelha o prototipo de responsividade).
  //
  // Subdivisao e entidades viram UM item so: no cabecalho elas ja sao um
  // unico controle (`SubdivisaoUnificada`, o mesmo chip abre as duas
  // escolhas em sequencia), entao separa-las no checklist so duplicava a
  // mesma acao em duas linhas.
  //
  // Simulacao antiga fica de fora: e uma via alternativa ao Estudo
  // Automatico (`selectWindxFiltrosObrigatoriosExcetoEntidades`), nao um
  // filtro obrigatorio — o checklist so lista o que realmente precisa ser
  // preenchido.
  //
  // "Tipo de rodada" aparece nas tres abas quando o painel tem a etapa
  // (`exibeRunType`, WEOL-SM #99): a consulta do Semanal e do Comparativo
  // tambem exige o tipo, e sem o item o checklist podia ficar todo feito com a
  // consulta ainda incompleta (o Comparativo ja abre com o ONS marcado).
  const entidadesOk = permiteSelecaoVazia || idsEntidades.length > 0;
  const checklistItens: ConsultaIncompletaItem[] = [
    {
      label: 'Subdivisão e entidades',
      meta: subdivisaoAtual
        ? `${subdivisaoAtual.label} · ${metaEntidades(idsEntidades.length, permiteSelecaoVazia)}`
        : 'toque para escolher',
      done: idSubdivisao !== undefined && entidadesOk,
      onClick: () => (idSubdivisao !== undefined ? setSeletorAberto(true) : abrirSubdivisao()),
    },
    ...(exibeRunType ? [{
      label: 'Tipo de rodada',
      meta: tipoRodada ?? 'preliminar ou final',
      done: !!tipoRodada,
      onClick: () => setPainelAberto(true),
    }] : []),
    {
      label: 'Modelos',
      meta: idsModelos.length > 0 ? `${idsModelos.length} marcado(s)` : 'nenhum marcado',
      done: idsModelos.length > 0,
      onClick: () => setPainelAberto(true),
    },
  ];
  // Guarda a ultima subdivisao observada para detectar mudanca real
  // (evita reabrir o sheet a cada render).
  const subdivAnteriorRef = useRef<number | undefined>(idSubdivisao);

  useEffect(() => {
    if (idSubdivisao === undefined) return;
    if (subdivAnteriorRef.current !== idSubdivisao) {
      // Abre o sheet apenas quando nao ha entidade selecionada. Ao restaurar
      // uma preferencia salva, setIdSubdivisaoEspacial zera idsEntidades no
      // mesmo batch em que setIdsEntidades(preferencia) e despachado — React
      // 18 consolida ambos antes do effect rodar, entao aqui idsEntidades ja
      // estara preenchido e o sheet NAO abre.
      if (idsEntidades.length === 0) {
        setSeletorAberto(true);
      }
      subdivAnteriorRef.current = idSubdivisao;
    }
  }, [idSubdivisao, idsEntidades]);

  return (
    <section className="windx-cabecalho-mobile" ref={raizRef}>
      <QueryCard>
        <QueryCardHeader
          label="Consulta atual"
          action={<span className="windx-cabecalho-mobile-gran">{granNote}</span>}
        >
          <QueryCardChips>
            <SubdivisaoUnificada
              tela={tela}
              onAbrirSeletor={() => setSeletorAberto(true)}
              permiteSelecaoVazia={permiteSelecaoVazia}
              apresentacaoEscolha="sheet"
            />
            {idsEntidades.length > 0 && (
              <button
                type="button"
                className="windx-cabecalho-mobile-limpar"
                aria-label="Limpar entidades selecionadas"
                onClick={() => dispatch(setIdsEntidades([]))}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={cssVar('text-muted')}
                  strokeWidth={2.4}
                  strokeLinecap="round"
                >
                  <path d="M6 6l12 12M18 6 6 18" />
                </svg>
              </button>
            )}
          </QueryCardChips>

          <Unit
            segmentado
            valor={unidade as Unidade}
            onChange={(v) => {
              dispatch(setUnidade(v));
              // Auto-disparo so com os obrigatorios preenchidos (decisao da PO
              // 2026-05-13: nada de request com painel incompleto).
              if (filtrosCompletos) {
                dispatch(realizarConsulta());
              }
            }}
          />
        </QueryCardHeader>

        <QueryCardTrigger
          onClick={() => setPainelAberto(true)}
          hint={resumoPainel}
          stacked
          dot={estimativa?.veredito}
          bordaSuperior
        />
      </QueryCard>

      <div className="windx-cabecalho-mobile-corpo">
        {filtrosCompletos ? children : (
          <ConsultaIncompletaCard
            hint={hintFiltrosIncompletos(tela, exibeRunType)}
            itens={checklistItens}
          />
        )}
      </div>

      <BarraConsulta carregando={carregando} onAbrirPainel={() => setPainelAberto(true)} />

      <ControlPanelSheet open={painelAberto} dismiss={() => setPainelAberto(false)} />

      <SeletorEntidadesSheet
        open={seletorAberto}
        dismiss={() => setSeletorAberto(false)}
        permiteSelecaoVazia={permiteSelecaoVazia}
      />
    </section>
  );
}
